import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { Report, GenerateReportRequest, PaginatedResponse } from '../types';
import toast from 'react-hot-toast';

export const useReports = (page: number = 1, perPage: number = 20) => {
    return useQuery({
        queryKey: ['reports', page, perPage],
        queryFn: async () => {
            const { data } = await api.get<{
                success: boolean;
                data: Report[];
                meta: PaginatedResponse<Report>['meta'];
            }>(`/reports?page=${page}&per_page=${perPage}`);
            return { data: data.data, meta: data.meta };
        },
    });
};

export const useReport = (reportId: string) => {
    return useQuery({
        queryKey: ['report', reportId],
        queryFn: async () => {
            const { data } = await api.get<{ success: boolean; data: Report }>(`/reports/${reportId}`);
            return data.data;
        },
        enabled: !!reportId,
    });
};

export const useGenerateReport = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (reportData: GenerateReportRequest) => {
            const { data } = await api.post<{
                success: boolean;
                data: { id: string; status: string };
                message: string;
            }>('/reports/generate', reportData);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['reports'] });
            toast.success(data.message || 'Report generation started');
        },
        onError: (error: any) => {
            if (error.response?.data?.error?.code === 'FEATURE_NOT_AVAILABLE') {
                const message = error.response.data.error.message;
                toast.error(`${message}. Try using JSON format or upgrade your plan.`, {
                    duration: 5000,
                });
            } else {
                toast.error('Failed to generate report');
            }
        },
    });
};

export const useDownloadReport = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (reportId: string) => {
            const response = await api.get(`/reports/${reportId}/download`, {
                responseType: 'blob', // IMPORTANT: Get as blob
            });

            // Get Content-Type from backend (already correct now)
            const contentType = response.headers['content-type'] || 'application/octet-stream';

            // Get filename from Content-Disposition header
            const contentDisposition = response.headers['content-disposition'];
            let filename = `report-${reportId}`;

            if (contentDisposition) {
                // Extract filename from Content-Disposition header
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            } else {
                // Fallback: determine extension from Content-Type if no filename in header
                let extension = '';
                if (contentType.includes('csv')) {
                    extension = '.csv';
                } else if (contentType.includes('pdf')) {
                    extension = '.pdf';
                } else if (contentType.includes('json')) {
                    extension = '.json';
                } else {
                    extension = '.txt';
                }
                filename += extension;
            }

            // Create blob with correct Content-Type from backend
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);

            // Create and trigger download link
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            // Cleanup
            link.remove();
            window.URL.revokeObjectURL(url);

            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['reports'] });
            toast.success('Report downloaded successfully');
        },
        onError: (error: any) => {
            if (error.response?.data?.error?.code === 'FILE_NOT_FOUND') {
                toast.error('Report file not found');
            } else if (error.response?.data?.error?.code === 'REPORT_EXPIRED') {
                toast.error('Report has expired');
            } else {
                toast.error('Failed to download report');
            }
        },
    });
};

export const useDeleteReport = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (reportId: string) => {
            await api.delete(`/reports/${reportId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['reports'] });
            toast.success('Report deleted successfully');
        },
        onError: () => {
            toast.error('Failed to delete report');
        },
    });
};

export const useRetryReport = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (reportId: string) => {
            const { data } = await api.post<{
                success: boolean;
                data: { id: string; status: string };
                message: string;
            }>(`/reports/${reportId}/retry`);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['reports'] });
            toast.success(data.message || 'Report generation restarted');
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.error?.message;
            if (error.response?.data?.error?.code === 'INVALID_STATUS') {
                toast.error(errorMessage || 'Only failed reports can be retried');
            } else {
                toast.error(errorMessage || 'Failed to retry report');
            }
        },
    });
};
